/* eslint consistent-return: 0, class-methods-use-this: 0 */
import { SystemSearchValueMap } from 'client/util/Constants';
import { get } from 'lodash';
import {
  getSearchField,
  setFilterTerm,
  setSearchTerm,
} from 'client/api/v2/apiUtils';
import CollectionApi from 'client/api/v2/CollectionApi';
import axios from 'axios';

class SystemApi extends CollectionApi {
  constructor() {
    super(
      '/api/systems', {
        skip: 0,
        limit: 50,
        sort: 'displayName',
        search: {
          fields: [
            'agentVersion',
            'amazonInstanceID',
            'arch',
            'displayName',
            'hostname',
            'networkInterfaces.address',
            'osFamily',
            'os',
            'remoteIP',
            'serialNumber',
            'version',
          ],
          searchTerm: '',
        },
      },
      100,
    );
  }

  getBoundUsers(id, params) {
    return axios.get(`/api/v2/systems/${id}/users`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  getBoundSystemGroups(id, params) {
    return axios.get(`/api/v2/systems/${id}/memberof`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  getBoundPolicies(id, params) {
    return axios.get(`/api/v2/systems/${id}/policies`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  getRecoveryKey(systemID) {
    const mesg = 'An error occurred while attempting to retrieve the Recovery Key. Please try again later.';
    return this.initialize()
      .then(() => axios.get(`/api/v2/systems/${systemID}/fdekey`))
      .then((resp) => {
        if (resp && resp.data) return resp.data;
      })
      .catch(() => {
        throw new Error(mesg);
      });
  }

  getSearchField(searchTerm) {
    return getSearchField(searchTerm, SystemSearchValueMap);
  }

  setSearchTerm(params, searchTerm) {
    return setSearchTerm(params, searchTerm, SystemSearchValueMap);
  }

  /**
   * @description Called from notifications workflow, when an admin is trying to
   * resolve user conflicts.
   */
  updateAuthConf(payload) {
    return this.initialize()
      .then(() => axios.post(`${this.baseUrl}/updateAuthConf`, payload).then(resp => resp.data));
  }

  fetchTotalCount(filters) {
    const formattedFilters = this.setFilterTerm(null, filters);
    const filterString = formattedFilters.length ? `?filter=${formattedFilters.join('&filter=')}` : '';
    return super.fetchTotalCount(this.baseUrl + filterString);
  }

  hasCustomFilter(obj) {
    // Custom filters are only available by themselves with no additional filters applied
    if (Array.isArray(obj) && obj.length === 1) {
      return obj.every((item) => (typeof item) !== 'string');
    }
    return false;
  }

  customQueryString(obj) {
    let key = '';
    const {
      value,
      customType,
      name,
      operator,
    } = obj;
    // For some system related api the custom filter must not contain the index
    // to handle this in the SystemFilters we have added a  key noIndex which is set to true
    // if this is true then the custom filter will not contain index
    if (obj.noIndex) {
      value.split('|').forEach((val, index) => {
        key += `${index !== 0 ? '&' : ''}filter[${customType}]=${name}:${operator}:${val}`;
      });
    } else {
      value.split('|').forEach((val, index) => {
        key += `${index !== 0 ? '&' : ''}filter[${customType}][${index}]=${name}:${operator}:${val}`;
      });
    }

    return key;
  }

  /**
   * Stringify an object of query string parameters for use in a GET request.
   * @see
   * https://stackoverflow.com/questions/1714786/query-string-encoding-of-a-javascript-object
   * http://api.jquery.com/jquery.param/
   */
  param(obj, prefix) {
    const str = [];

    Object.keys(obj).forEach((property) => {
      if (Object.prototype.hasOwnProperty.call(obj, property)) {
        let key;
        /**
         * Custom Query string for systems v1 endpoint.
         * as of 1/2022 this is the only endpoint we are doing this for
         * currently this only allows the option of one custom filter
         * with no additional filters applied
         */

        if (property === 'filter') {
          if (this.hasCustomFilter(obj.filter)) {
            str.push(this.customQueryString(obj.filter[0]));
            return;
          }
        }

        if (prefix && prefix === 'filter' && typeof obj[property] === 'object' && obj[property].logicalOperator) {
          key = `${prefix}[${obj[property].logicalOperator}][${property}]`;
        } else {
          key = prefix ? `${prefix}[${property}]` : property;
        }

        const value = get(obj[property], 'filterValue', obj[property]);
        if (value !== null && typeof value === 'object') {
          // Recurse to process nested objects
          str.push(this.param(value, key));
        } else {
          const typeOfValue = typeof value;
          // Add the key/value pair to the URL if the value is a boolean, number or non-empty string
          if (typeOfValue === 'boolean'
                    || typeOfValue === 'number'
                    || (typeOfValue === 'string' && value.trim().length !== 0)) {
            str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
          }
        }
      }
    });
    return str.join('&');
  }

  /**
   * @param {string} systemId - System Id
   * @param {Object} payload - type, name
   */
  postEraseDevice(systemId, payload) {
    return this.axios.post(`/api/systems/${systemId}/command/builtin/erase`, payload);
  }

  /**
   * @param {string} systemId - System Id
   * @param {Object} payload - type, name
   */
  postLockDevice(systemId, payload) {
    return this.axios.post(`/api/systems/${systemId}/command/builtin/lock`, payload);
  }

  /**
   * @param {string} systemId - System Id
   * @param {Object} payload - type, name
   */
  postRestartDevice(systemId, payload) {
    return this.axios.post(`/api/systems/${systemId}/command/builtin/restart`, payload);
  }

  /**
   * @param {string} systemId - System Id
   * @param {Object} payload - type, name
   */
  postShutdownDevice(systemId, payload) {
    return this.axios.post(`/api/systems/${systemId}/command/builtin/shutdown`, payload);
  }

  runAgentLogCommand(systemId) {
    return this.initialize()
      .then(() => axios.post(`${this.baseUrl}/${systemId}/runAgentLogCommand`))
      .then(resp => resp.data);
  }

  async search(payload) {
    await this.initialize();
    return axios.post('/api/search/systems', payload);
  }
}

const api = new SystemApi();
api.setFilterTerm = setFilterTerm;
export default api;
